"use client";


import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const services = [
  {
    icon: "MagnifyingGlassIcon",
    title: "Product Sourcing",
    desc: "Direct factory access in Guangdong. We find, vet, and negotiate with suppliers so you get the best price without the risk.",
  },
  {
    icon: "BuildingStorefrontIcon",
    title: "Warehousing",
    desc: "Secure, climate-controlled storage in Shenzhen. Real-time inventory tracking with low-stock alerts before you run out.",
  },
  {
    icon: "ArchiveBoxIcon",
    title: "Pick & Pack",
    desc: "Accurate fulfillment with quality checks on every order. Under 0.3% error rate across all shipments.",
  },
  {
    icon: "TruckIcon",
    title: "Worldwide Shipping",
    desc: "US in 5–8 days. EU in 5–8 days. UK in 4–7 days. Competitive rates via our carrier network with live tracking.",
  },
  {
    icon: "GiftIcon",
    title: "Custom Packaging",
    desc: "Branded boxes, inserts, and private labeling. Your product arrives looking premium — not like a dropship package.",
  },
  {
    icon: "ArrowPathIcon",
    title: "Returns Management",
    desc: "Hassle-free returns processing with quality inspection and restocking. Full reporting on return reasons.",
  },
];

export default function ServicesOverview() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-secondary-dark" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="flex flex-col md:flex-row md:items-end justify-between mb-12 sm:mb-14 lg:mb-16 gap-6"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-glass-white border border-glass-border text-xs sm:text-sm font-medium text-text-secondary backdrop-blur-xl mb-3 sm:mb-4">
              Full-Stack Logistics
            </span>
            <h2 
              className="font-heading font-extrabold text-text-primary" 
              style={{ fontSize: "clamp(28px, 5vw, 48px)", letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              Everything from factory to{" "}
              <span className="gradient-text">front door</span>
            </h2>
          </div>
          <Link href="/services" className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 rounded-2xl bg-glass-white border border-glass-border text-text-primary font-semibold text-sm sm:text-base backdrop-blur-xl transition-smooth hover:bg-opacity-10 hover:border-accent-primary shrink-0 min-h-[48px] w-full md:w-auto">
            View All Services
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {services?.map((service, index) => (
            <motion.div
              key={service?.title}
              className="glass-card rounded-3xl sm:rounded-4xl p-6 sm:p-8 border border-glass-border backdrop-blur-xl transition-all duration-500 hover:shadow-glass-hover hover:-translate-y-1"
              variants={cardVariants}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center mb-4 sm:mb-5">
                <Icon name={service?.icon} size={24} className="text-accent-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold font-heading text-text-primary mb-2 sm:mb-3">
                {service?.title}
              </h3>
              <p className="text-sm sm:text-base text-text-secondary leading-relaxed">
                {service?.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}